import React, { useEffect, useMemo, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  FormControl,
  RadioGroup,
  Radio,
  TextField,
  Checkbox,
  Chip,
  Avatar,
  IconButton,
  FormControlLabel,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { axiosInstance } from '../../../utils/tokenUtils';
import './ReportModal.css';
import CancelIcon from '../../../assets/Model/reject-icon.svg';

// API enum values for report reasons
const REPORT_REASON_ENUM = {
  STUDENT_DID_NOT_ATTEND: 'student_did_not_attend_the_session',
  STUDENT_MISBEHAVIOR: 'student_misbehavior',
  INAPPROPRIATE_CONTENT: 'inappropriate_content_shared',
  TECHNICAL_ISSUES: 'technical_issues',
  KOINS_ISSUES: 'koins_deduction_issues',
  PAYMENT_ISSUES: 'payment_issues',
  OTHER: 'other',
};

const ReportModal = ({ open, onClose, sessionDetails, onSuccess }) => {
  const [selectedReason, setSelectedReason] = useState('');
  const [description, setDescription] = useState('');
  const [selectedStudents, setSelectedStudents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [file, setFile] = useState(null);

  const isOneOnOne = useMemo(
    () =>
      sessionDetails?.session_type === 'one_on_one' ||
      (sessionDetails?.students?.length || 0) === 1,
    [sessionDetails]
  );

  // Reasons as per new UX → API values
  const reportReasons = [
    {
      display: 'Learner did not attend the session.',
      value: REPORT_REASON_ENUM.STUDENT_DID_NOT_ATTEND,
    },
    {
      display: 'Learner misbehavior during session.',
      value: REPORT_REASON_ENUM.STUDENT_MISBEHAVIOR,
    },
    { display: 'Inappropriate Content', value: REPORT_REASON_ENUM.INAPPROPRIATE_CONTENT },
    {
      display: "Facing technical issues? Let us know, and we'll get it fixed ASAP.",
      value: REPORT_REASON_ENUM.TECHNICAL_ISSUES,
    },
    { display: 'Issues with Koins deduction', value: REPORT_REASON_ENUM.KOINS_ISSUES },
    { display: 'Payment issues', value: REPORT_REASON_ENUM.PAYMENT_ISSUES },
    { display: 'Other issues', value: REPORT_REASON_ENUM.OTHER },
  ];

  // Preselect the only learner for one-on-one sessions
  useEffect(() => {
    if (!open) return;
    if (isOneOnOne && sessionDetails?.students?.[0]?.id) {
      setSelectedStudents([sessionDetails.students[0].id]);
    } else {
      setSelectedStudents([]);
    }
  }, [open, isOneOnOne, sessionDetails]);

  const handleStudentToggle = studentId => {
    setSelectedStudents(prev =>
      prev.includes(studentId) ? prev.filter(id => id !== studentId) : [...prev, studentId]
    );
  };

  const handleFileChange = e => {
    const f = e.target.files?.[0];
    setFile(f || null);
  };

  const countWords = text => (text?.trim() ? text.trim().split(/\s+/).length : 0);
  const maxWords = 500;

  const handleSubmit = async () => {
    if (!selectedReason) {
      setError('Please select a reason');
      return;
    }

    if (!isOneOnOne && selectedStudents.length === 0) {
      setError('Please select at least one learner');
      return;
    }

    try {
      setLoading(true);
      setError('');

      const body = {
        booking_id: sessionDetails?.id,
        reason: selectedReason,
        description: description || selectedReason,
        student_ids: isOneOnOne
          ? [sessionDetails?.students?.[0]?.id].filter(Boolean)
          : selectedStudents,
      };

      // Step 1: create dispute
      const createRes = await axiosInstance.post('/teachers/sessions/dispute', body);
      const disputeId = createRes?.data?.id;

      // Step 2: optional file upload
      if (file && disputeId) {
        const form = new FormData();
        form.append('file', file);
        await axiosInstance.post(`/teachers/sessions/dispute/${disputeId}/upload`, form, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
      }

      onSuccess?.();
      handleClose();
    } catch (err) {
      console.error('Error creating dispute:', err);
      if (err?.response) {
        const status = err.response.status;
        const errorDetail =
          err.response.data?.detail || err.response.data?.message || 'Unable to submit dispute';
        if (status === 400) setError(errorDetail);
        else if (status === 401 || status === 403)
          setError('You are not authorized to perform this action');
        else if (status === 404) setError('Session not found');
        else if (status === 500) setError('Internal Server Error. Please try again later.');
        else setError(`Error: ${errorDetail}`);
      } else if (err?.request) {
        setError('No response from server. Please check your internet connection.');
      } else {
        setError('An unexpected error occurred. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setSelectedReason('');
    setDescription('');
    setSelectedStudents([]);
    setError('');
    setLoading(false);
    setFile(null);
    onClose();
  };

  const isFormValid = useMemo(() => {
    const reasonOk = Boolean(selectedReason);
    const studentsOk = isOneOnOne
      ? Boolean(sessionDetails?.students?.[0]?.id)
      : selectedStudents.length > 0;
    return reasonOk && studentsOk;
  }, [selectedReason, isOneOnOne, selectedStudents, sessionDetails]);

  const studentList = sessionDetails?.students || [];

  const startTimeText = useMemo(() => {
    try {
      const start = sessionDetails?.start_time ? new Date(sessionDetails.start_time) : null;
      const end = sessionDetails?.end_time
        ? new Date(sessionDetails.end_time)
        : sessionDetails?.length_in_minutes
          ? new Date(
              new Date(sessionDetails.start_time).getTime() +
                sessionDetails.length_in_minutes * 60000
            )
          : null;
      const dateStr = start ? start.toLocaleDateString() : '---';
      const startStr = start
        ? start.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })
        : '---';
      const endStr = end
        ? end.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })
        : '---';
      return { dateStr, timeStr: `${startStr} - ${endStr}` };
    } catch {
      return { dateStr: '---', timeStr: '---' };
    }
  }, [sessionDetails]);

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      className="report-modal-dialog"
    >
      <DialogTitle className="report-modal-header">
        <Typography variant="h6" className="report-modal-title">
          Raise Dispute
        </Typography>
        <IconButton onClick={handleClose} className="report-modal-close-btn" size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent className="report-modal-body">
        {error && (
          <Box
            className="report-error-section"
            sx={{ mb: 2, p: 2, bgcolor: '#ffebee', borderRadius: 1 }}
          >
            <Typography color="error" variant="body2">
              {error}
            </Typography>
          </Box>
        )}

        {/* Subtle overview (date/time only) */}
        {/* <Box className="report-session-overview">
          <Typography className="overview-heading">Session Overview</Typography>
          <Box className="overview-card">
            <Box className="overview-left">
              <Avatar
                src={sessionDetails?.students?.[0]?.user?.profile_image}
                alt={sessionDetails?.students?.[0]?.user?.name}
                className="overview-avatar"
              />
              <Box className="overview-main">
                <Typography className="overview-name">
                  {sessionDetails?.students?.[0]?.user?.name || 'Learner'}
                </Typography>
                <Box className="overview-tags">
                  <Typography className="overview-subject">
                    {sessionDetails?.subject || '---'}
                  </Typography>
                  {sessionDetails?.session_type && (
                    <Typography className="overview-type">
                      {sessionDetails.session_type?.replace(/_/g, ' ')}
                    </Typography>
                  )}
                </Box>
              </Box>
            </Box>
            <Box className="overview-right">
              <Box className="overview-date-time">
                <Box className="date-block">
                  <Typography className="label">Date</Typography>
                  <Typography className="value">{startTimeText.dateStr}</Typography>
                </Box>
                <Box className="time-block">
                  <Typography className="label">Timing</Typography>
                  <Typography className="value">{startTimeText.timeStr}</Typography>
                </Box>
              </Box>
            </Box>
          </Box>
        </Box> */}

        {/* Hero / warning */}
        <Box className="report-warning-section">
          <Box className="report-warning-icon-container">
            <img src={CancelIcon} alt="warning" className="report-warning-icon" />
          </Box>
          <Typography className="report-warning-text">
            Tell us what went wrong during your session.
          </Typography>
          <Typography className="report-subtext">
            Provide as much detail as possible to help us resolve the issue
          </Typography>
        </Box>

        {/* Reasons - radio list */}
        <Box className="report-form-section report-card">
          <Typography className="report-section-label" sx={{ mb: 1 }}>
            Select Reason *
          </Typography>
          <FormControl fullWidth className="report-reason-radio-group">
            <RadioGroup value={selectedReason} onChange={e => setSelectedReason(e.target.value)}>
              {reportReasons.map(reason => (
                <FormControlLabel
                  key={reason.value}
                  value={reason.value}
                  control={<Radio />}
                  label={reason.display}
                  className={selectedReason === reason.value ? 'selected-reason' : ''}
                />
              ))}
            </RadioGroup>
          </FormControl>
        </Box>

        {/* Additional details */}
        <Box className="report-form-section report-card details-card">
          <Box className="details-header">
            <Typography className="report-section-label">Additional details</Typography>
            <Typography className="word-limit">{maxWords} Words</Typography>
          </Box>
          <TextField
            multiline
            rows={4}
            value={description}
            onChange={e => {
              const words = e.target.value;
              if (countWords(words) <= maxWords) setDescription(words);
            }}
            placeholder="Enter here..."
            variant="outlined"
            fullWidth
            className="report-details-textfield"
          />
        </Box>

        {/* Student selection (hide for one-on-one) */}
        {!isOneOnOne && (
          <Box className="report-form-section report-card">
            <Typography className="report-section-label">Select Learners to Report *</Typography>
            <Typography className="report-section-description">
              Choose the learners who were involved in the reported incident
            </Typography>

            <Box className="report-students-container">
              {studentList.map(student => (
                <Box key={student?.id} className="report-student-item">
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={selectedStudents.includes(student?.id)}
                        onChange={() => handleStudentToggle(student?.id)}
                        className="report-student-checkbox"
                      />
                    }
                    label={
                      <Box className="report-student-info">
                        <Avatar
                          src={student?.user?.profile_image}
                          alt={student?.user?.name}
                          className="report-student-avatar"
                        />
                        <Box className="report-student-details">
                          <Typography className="report-student-name">
                            {student?.user?.name || 'Unknown Learner'}
                          </Typography>
                        </Box>
                      </Box>
                    }
                    className="report-student-label"
                  />
                </Box>
              ))}
            </Box>

            {selectedStudents.length > 0 && (
              <Box className="report-selected-students">
                <Typography className="report-selected-count">
                  {selectedStudents.length} learner(s) selected
                </Typography>
                <Box className="report-selected-chips">
                  {studentList
                    .filter(student => selectedStudents.includes(student?.id))
                    .map(student => (
                      <Chip
                        key={student?.id}
                        label={student?.user?.name || 'Unknown Learner'}
                        onDelete={() => handleStudentToggle(student?.id)}
                        className="report-student-chip"
                        avatar={
                          <Avatar
                            src={student?.user?.profile_image}
                            alt={student?.user?.name}
                            className="report-chip-avatar"
                          />
                        }
                      />
                    ))}
                </Box>
              </Box>
            )}
          </Box>
        )}

        {/* Attachments */}
        <Box className="report-form-section">
          <Box
            className="attach-file-box"
            onClick={() => document.getElementById('dispute-file-input')?.click()}
          >
            <input
              id="dispute-file-input"
              type="file"
              style={{ display: 'none' }}
              onChange={handleFileChange}
            />
            <Box className="attach-icon" />
            <Typography className="attach-text">Attach Files</Typography>
            {file && <Typography className="file-name">{file.name}</Typography>}
          </Box>
        </Box>
      </DialogContent>

      <DialogActions className="report-modal-footer">
        <Button onClick={handleClose} className="report-cancel-button" disabled={loading}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={!isFormValid || loading}
          className="report-submit-button"
        >
          {loading ? 'Submitting...' : 'Submit'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ReportModal;
